import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import { COL } from 'shared/darkColors';
import {
  FiCpu,
  FiBook,
  FiUser,
  FiShield,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiToggleLeft,
  FiToggleRight,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiAlertCircle,
  FiInfo,
  FiStar,
  FiLock,
} from 'react-icons/fi';

function BotIA() {
  const { tienePermiso } = useAuth();
  const puedeEditar = tienePermiso('bot.editar');
  const [tab, setTab] = useState('conocimientos');
  const [, setConfig] = useState(null);
  const [skills, setSkills] = useState([]);
  const [uso, setUso] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [editSkill, setEditSkill] = useState(null);
  const [nuevoSkill, setNuevoSkill] = useState(null);
  const [expandedSkill, setExpandedSkill] = useState(null);
  const [toast, setToast] = useState(null);

  const [configForm, setConfigForm] = useState({
    prompt_sistema: '',
    conocimiento_extra: '',
    fase_bienvenida: '',
    fase_descubrimiento: '',
    fase_negociacion: '',
    fase_cierre: '',
    fase_postventa: '',
    horario_semana: '',
    horario_finde: '',
    zonas_cobertura: '',
    metodos_pago: '',
    politicas_devolucion: '',
    notas_negocio: '',
    pedido_minimo_usd: '',
    costo_envio_usd: '',
    envio_gratis_min_usd: '',
    tiempo_entrega_min: '',
    tiempo_entrega_max: '',
  });

  const mostrarToast = useCallback((msg, tipo = 'success') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const [cfgRes, skillsRes, usoRes] = await Promise.all([
        API.get('/empresa-ia/config'),
        API.get('/empresa-ia/skills'),
        API.get('/empresa-ia/uso'),
      ]);
      const cfg = cfgRes.data.data;
      setConfig(cfg);
      if (cfg) {
        setConfigForm({
          prompt_sistema: cfg.prompt_sistema || '',
          conocimiento_extra: cfg.conocimiento_extra || '',
          fase_bienvenida: cfg.fase_bienvenida || '',
          fase_descubrimiento: cfg.fase_descubrimiento || '',
          fase_negociacion: cfg.fase_negociacion || '',
          fase_cierre: cfg.fase_cierre || '',
          fase_postventa: cfg.fase_postventa || '',
          horario_semana: cfg.horario_semana || '',
          horario_finde: cfg.horario_finde || '',
          zonas_cobertura: cfg.zonas_cobertura || '',
          metodos_pago: cfg.metodos_pago || '',
          politicas_devolucion: cfg.politicas_devolucion || '',
          notas_negocio: cfg.notas_negocio || '',
          pedido_minimo_usd: cfg.pedido_minimo_usd != null ? String(cfg.pedido_minimo_usd) : '',
          costo_envio_usd: cfg.costo_envio_usd != null ? String(cfg.costo_envio_usd) : '',
          envio_gratis_min_usd:
            cfg.envio_gratis_min_usd != null ? String(cfg.envio_gratis_min_usd) : '',
          tiempo_entrega_min: cfg.tiempo_entrega_min != null ? String(cfg.tiempo_entrega_min) : '',
          tiempo_entrega_max: cfg.tiempo_entrega_max != null ? String(cfg.tiempo_entrega_max) : '',
        });
      }
      setSkills(skillsRes.data.data || []);
      setUso(usoRes.data.data || null);
    } catch (e) {
      console.error('Error cargando datos IA:', e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const guardarConfig = async () => {
    setGuardando(true);
    try {
      const body = {};
      Object.entries(configForm).forEach(([k, v]) => {
        if (v !== '' && v !== null) {
          if (['pedido_minimo_usd', 'costo_envio_usd', 'envio_gratis_min_usd'].includes(k)) {
            body[k] = parseFloat(v) || null;
          } else if (['tiempo_entrega_min', 'tiempo_entrega_max'].includes(k)) {
            body[k] = parseInt(v) || null;
          } else {
            body[k] = v;
          }
        }
      });
      await API.put('/empresa-ia/config', body);
      mostrarToast('Configuración guardada');
      cargarDatos();
    } catch (e) {
      mostrarToast(e.response?.data?.detail || 'Error al guardar', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const toggleSkill = async (skill) => {
    if (!skill.id) return;
    try {
      await API.put(`/empresa-ia/skills/${skill.id}`, { activo: !skill.activo });
      cargarDatos();
    } catch (e) {
      mostrarToast('Error al cambiar estado', 'error');
    }
  };

  const guardarSkill = async () => {
    if (!editSkill) return;
    setGuardando(true);
    try {
      await API.put(`/empresa-ia/skills/${editSkill.id}`, {
        titulo: editSkill.titulo,
        contenido: editSkill.contenido,
      });
      mostrarToast('Skill actualizado');
      setEditSkill(null);
      cargarDatos();
    } catch (e) {
      mostrarToast('Error al guardar skill', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const crearSkill = async () => {
    if (!nuevoSkill?.titulo || !nuevoSkill?.contenido) return;
    setGuardando(true);
    try {
      await API.post('/empresa-ia/skills', {
        titulo: nuevoSkill.titulo,
        contenido: nuevoSkill.contenido,
      });
      mostrarToast('Skill creado');
      setNuevoSkill(null);
      cargarDatos();
    } catch (e) {
      mostrarToast('Error al crear skill', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarSkill = async (skill) => {
    if (!skill.id || !window.confirm(`Eliminar "${skill.titulo}"?`)) return;
    try {
      await API.delete(`/empresa-ia/skills/${skill.id}`);
      mostrarToast(skill.es_base ? 'Skill restaurado a default del sistema' : 'Skill eliminado');
      cargarDatos();
    } catch (e) {
      mostrarToast('Error al eliminar', 'error');
    }
  };

  const TABS = [
    { id: 'conocimientos', icon: FiBook, label: 'Conocimientos' },
    { id: 'personalidad', icon: FiUser, label: 'Personalidad' },
    { id: 'politicas', icon: FiShield, label: 'Políticas' },
  ];

  const pctColor =
    (uso?.completitud_pct || 0) >= 70
      ? COL.acc
      : (uso?.completitud_pct || 0) >= 40
        ? COL.amber
        : COL.danger;

  if (cargando)
    return (
      <div style={S.loading}>
        <FiCpu size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Cargando configuración IA...</span>
      </div>
    );

  return (
    <div style={S.container}>
      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @media (max-width:768px) {
          .botia-grid { flex-direction:column !important; }
          .botia-tabs { overflow-x:auto !important; flex-wrap:nowrap !important; }
        }
      `}</style>

      {toast && (
        <div style={{ ...S.toast, background: toast.tipo === 'error' ? COL.danger : COL.acc }}>
          {toast.msg}
        </div>
      )}

      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FiCpu size={24} color={COL.pri2} />
          <div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: COL.pri }}>
              Bot de Inteligencia Artificial
            </div>
            <div style={{ fontSize: '13px', color: COL.muted }}>
              Configura como tu bot interactua con los clientes
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {uso && (
            <>
              <div style={S.statBadge}>
                <FiStar size={14} color={COL.amber} />
                <span>{uso.completitud_pct}% completo</span>
                <div
                  style={{
                    width: '60px',
                    height: '6px',
                    borderRadius: '3px',
                    background: COL.border,
                  }}
                >
                  <div
                    style={{
                      width: `${uso.completitud_pct}%`,
                      height: '100%',
                      borderRadius: '3px',
                      background: pctColor,
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
              </div>
              {uso.mensajes_limite != null && (
                <div
                  style={{
                    ...S.statBadge,
                    background:
                      uso.mensajes_usados >= uso.mensajes_limite ? '#fef2f2' : 'var(--agx-blue-bg)',
                    color: uso.mensajes_usados >= uso.mensajes_limite ? COL.danger : COL.pri2,
                  }}
                >
                  <FiAlertCircle size={14} />
                  <span>
                    {uso.mensajes_usados}/{uso.mensajes_limite} msgs IA
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!puedeEditar && (
        <div style={S.readonlyBanner}>
          <FiLock size={16} />
          <span>
            Solo tienes acceso de lectura. No puedes modificar la configuración del Bot IA. Contacta
            al administrador si necesitas editar.
          </span>
        </div>
      )}

      <div className="botia-tabs" style={S.tabs}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{ ...S.tab, ...(tab === t.id ? S.tabActive : {}) }}
          >
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="botia-grid" style={S.mainGrid}>
        <div style={S.content}>
          {tab === 'conocimientos' && renderConocimientos()}
          {tab === 'personalidad' && renderPersonalidad()}
          {tab === 'politicas' && renderPoliticas()}
        </div>
      </div>

      {editSkill && renderEditModal()}
      {nuevoSkill && renderNuevoModal()}
    </div>
  );

  function renderConocimientos() {
    const activos = skills.filter((s) => s.activo);
    const inactivos = skills.filter((s) => !s.activo);
    return (
      <div>
        <div style={S.sectionHeader}>
          <div>
            <div style={S.sectionTitle}>Bloques de conocimiento</div>
            <div style={S.sectionDesc}>
              El bot usa estos bloques para responder a tus clientes. Puedes editarlos, crear nuevos
              o desactivar los que no necesites.
            </div>
          </div>
          {puedeEditar && (
            <button
              onClick={() => setNuevoSkill({ titulo: '', contenido: '' })}
              style={S.btnPrimary}
            >
              <FiPlus size={14} /> Nuevo
            </button>
          )}
        </div>

        {skills.length === 0 && (
          <div style={S.emptyState}>
            <FiInfo size={32} color={COL.muted} />
            <div style={{ fontSize: '15px', fontWeight: 600, color: COL.text }}>
              Sin conocimientos personalizados
            </div>
            <div style={{ fontSize: '13px', color: COL.muted }}>
              Tu bot está usando los conocimientos genericos del sistema. Usa el boton de Asistente
              IA en la esquina inferior derecha para crear conocimientos adaptados a tu negocio.
            </div>
          </div>
        )}

        {activos.map((skill) => renderSkillCard(skill))}

        {inactivos.length > 0 && (
          <>
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: COL.muted,
                marginTop: '20px',
                marginBottom: '8px',
              }}
            >
              Desactivados ({inactivos.length})
            </div>
            {inactivos.map((skill) => renderSkillCard(skill))}
          </>
        )}
      </div>
    );
  }

  function renderSkillCard(skill) {
    const isExpanded = expandedSkill === (skill.id || skill.source_key);
    const isBase = skill.es_base || skill._from_md;
    const isOverridden = skill._overridden;
    return (
      <div
        key={skill.id || skill.source_key}
        style={{
          ...S.skillCard,
          opacity: skill.activo ? 1 : 0.5,
          borderLeft: `3px solid ${isBase ? COL.pri2 : COL.purple}`,
        }}
      >
        <div
          style={S.skillCardHeader}
          onClick={() => setExpandedSkill(isExpanded ? null : skill.id || skill.source_key)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: COL.text }}>{skill.titulo}</div>
            {isBase && <span style={S.badgeBlue}>base</span>}
            {!isBase && <span style={S.badgePurple}>custom</span>}
            {isOverridden && <span style={S.badgeAmber}>modificado</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {puedeEditar && skill.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSkill(skill);
                }}
                style={S.btnIcon}
                title={skill.activo ? 'Desactivar' : 'Activar'}
              >
                {skill.activo ? (
                  <FiToggleRight size={18} color={COL.acc} />
                ) : (
                  <FiToggleLeft size={18} color={COL.muted} />
                )}
              </button>
            )}
            {puedeEditar && skill.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditSkill({ ...skill });
                }}
                style={S.btnIcon}
              >
                <FiEdit2 size={14} color={COL.pri2} />
              </button>
            )}
            {puedeEditar && skill.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  eliminarSkill(skill);
                }}
                style={S.btnIcon}
              >
                <FiTrash2 size={14} color={COL.danger} />
              </button>
            )}
            {isExpanded ? (
              <FiChevronUp size={16} color={COL.muted} />
            ) : (
              <FiChevronDown size={16} color={COL.muted} />
            )}
          </div>
        </div>
        {!isExpanded && <div style={S.skillPreview}>{skill.contenido?.substring(0, 120)}...</div>}
        {isExpanded && (
          <div style={S.skillContent}>
            <pre style={S.skillPre}>{skill.contenido}</pre>
          </div>
        )}
      </div>
    );
  }

  function renderPersonalidad() {
    return (
      <div>
        <div style={S.sectionHeader}>
          <div>
            <div style={S.sectionTitle}>Personalidad del Bot</div>
            <div style={S.sectionDesc}>
              Define como se comporta tu bot: su prompt principal y las fases de la conversacion de
              venta.
            </div>
          </div>
          {puedeEditar && (
            <button onClick={guardarConfig} disabled={guardando} style={S.btnPrimary}>
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          )}
        </div>

        <div style={S.formCard}>
          <label style={S.label}>Prompt del sistema</label>
          <div style={S.labelHint}>
            Instrucciones principales del bot. Usa {'{nombre_bot}'} como placeholder.
          </div>
          <textarea
            value={configForm.prompt_sistema}
            onChange={(e) => setConfigForm((p) => ({ ...p, prompt_sistema: e.target.value }))}
            style={{ ...S.textarea, minHeight: '120px' }}
            placeholder="Eres {nombre_bot}, asistente virtual de mi negocio..."
            readOnly={!puedeEditar}
          />
        </div>

        <div style={S.formCard}>
          <label style={S.label}>Fases de venta</label>
          <div style={S.labelHint}>
            Personaliza como el bot maneja cada fase de la conversacion con el cliente.
          </div>
          {[
            { key: 'fase_bienvenida', label: 'Bienvenida', desc: 'Primer contacto con el cliente' },
            {
              key: 'fase_descubrimiento',
              label: 'Descubrimiento',
              desc: 'Entender que necesita el cliente',
            },
            { key: 'fase_negociacion', label: 'Negociacion', desc: 'Manejar precios y objeciones' },
            { key: 'fase_cierre', label: 'Cierre', desc: 'Confirmar el pedido' },
            {
              key: 'fase_postventa',
              label: 'Post-venta',
              desc: 'Seguimiento despues de la compra',
            },
          ].map((fase) => (
            <div key={fase.key} style={{ marginBottom: '12px' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div style={{ fontSize: '13px', fontWeight: 600, color: COL.text }}>
                  {fase.label}
                </div>
                <div style={{ fontSize: '11px', color: COL.muted }}>{fase.desc}</div>
              </div>
              <textarea
                value={configForm[fase.key]}
                onChange={(e) => setConfigForm((p) => ({ ...p, [fase.key]: e.target.value }))}
                style={{ ...S.textarea, minHeight: '70px' }}
                placeholder={`Instrucciones para la fase de ${fase.label.toLowerCase()}...`}
                readOnly={!puedeEditar}
              />
            </div>
          ))}
        </div>

        <div style={S.formCard}>
          <label style={S.label}>Conocimiento extra</label>
          <div style={S.labelHint}>
            Info adicional que el bot debe conocer (no cubierta por los skills).
          </div>
          <textarea
            value={configForm.conocimiento_extra}
            onChange={(e) => setConfigForm((p) => ({ ...p, conocimiento_extra: e.target.value }))}
            style={{ ...S.textarea, minHeight: '80px' }}
            placeholder="Informacion adicional sobre tu negocio..."
            readOnly={!puedeEditar}
          />
        </div>
      </div>
    );
  }

  function renderPoliticas() {
    return (
      <div>
        <div style={S.sectionHeader}>
          <div>
            <div style={S.sectionTitle}>Políticas y datos del negocio</div>
            <div style={S.sectionDesc}>
              Información que el bot usa para responder sobre envíos, horarios, pagos y políticas.
            </div>
          </div>
          {/* Botones de accion para Politicas */}
          {puedeEditar && (
            <button onClick={guardarConfig} disabled={guardando} style={S.btnPrimary}>
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          )}
        </div>

        <div style={S.formCard}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label style={S.label}>Pedido mínimo (USD)</label>
              <input
                type="number"
                value={configForm.pedido_minimo_usd}
                onChange={(e) =>
                  setConfigForm((p) => ({ ...p, pedido_minimo_usd: e.target.value }))
                }
                style={S.input}
                placeholder="0.00"
                readOnly={!puedeEditar}
              />
            </div>
            <div>
              <label style={S.label}>Costo envio (USD)</label>
              <input
                type="number"
                value={configForm.costo_envio_usd}
                onChange={(e) => setConfigForm((p) => ({ ...p, costo_envio_usd: e.target.value }))}
                style={S.input}
                placeholder="0.00"
                readOnly={!puedeEditar}
              />
            </div>
            <div>
              <label style={S.label}>Envio gratis desde (USD)</label>
              <input
                type="number"
                value={configForm.envio_gratis_min_usd}
                onChange={(e) =>
                  setConfigForm((p) => ({ ...p, envio_gratis_min_usd: e.target.value }))
                }
                style={S.input}
                placeholder="0.00"
                readOnly={!puedeEditar}
              />
            </div>
          </div>
        </div>

        <div style={S.formCard}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={S.label}>Tiempo entrega minimo (min)</label>
              <input
                type="number"
                value={configForm.tiempo_entrega_min}
                onChange={(e) =>
                  setConfigForm((p) => ({ ...p, tiempo_entrega_min: e.target.value }))
                }
                style={S.input}
                placeholder="30"
                readOnly={!puedeEditar}
              />
            </div>
            <div>
              <label style={S.label}>Tiempo entrega máximo (min)</label>
              <input
                type="number"
                value={configForm.tiempo_entrega_max}
                onChange={(e) =>
                  setConfigForm((p) => ({ ...p, tiempo_entrega_max: e.target.value }))
                }
                style={S.input}
                placeholder="60"
                readOnly={!puedeEditar}
              />
            </div>
          </div>
        </div>

        <div style={S.formCard}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={S.label}>Horario entre semana</label>
              <input
                value={configForm.horario_semana}
                onChange={(e) => setConfigForm((p) => ({ ...p, horario_semana: e.target.value }))}
                style={S.input}
                placeholder="8:00 AM - 6:00 PM"
                readOnly={!puedeEditar}
              />
            </div>
            <div>
              <label style={S.label}>Horario fin de semana</label>
              <input
                value={configForm.horario_finde}
                onChange={(e) => setConfigForm((p) => ({ ...p, horario_finde: e.target.value }))}
                style={S.input}
                placeholder="9:00 AM - 1:00 PM"
                readOnly={!puedeEditar}
              />
            </div>
          </div>
        </div>

        <div style={S.formCard}>
          <label style={S.label}>Zonas de cobertura</label>
          <textarea
            value={configForm.zonas_cobertura}
            onChange={(e) => setConfigForm((p) => ({ ...p, zonas_cobertura: e.target.value }))}
            style={S.textarea}
            placeholder="Zonas donde realizas entregas..."
            readOnly={!puedeEditar}
          />
        </div>

        <div style={S.formCard}>
          <label style={S.label}>Métodos de pago aceptados</label>
          <textarea
            value={configForm.metodos_pago}
            onChange={(e) => setConfigForm((p) => ({ ...p, metodos_pago: e.target.value }))}
            style={S.textarea}
            placeholder="Efectivo, Pago Movil, Zelle, Binance..."
            readOnly={!puedeEditar}
          />
        </div>

        <div style={S.formCard}>
          <label style={S.label}>Políticas de devolución</label>
          <textarea
            value={configForm.politicas_devolucion}
            onChange={(e) => setConfigForm((p) => ({ ...p, politicas_devolucion: e.target.value }))}
            style={S.textarea}
            placeholder="Describe tus politicas de devolucion..."
            readOnly={!puedeEditar}
          />
        </div>

        <div style={S.formCard}>
          <label style={S.label}>Notas del negocio</label>
          <div style={S.labelHint}>Cualquier otra informacion relevante para el bot.</div>
          <textarea
            value={configForm.notas_negocio}
            onChange={(e) => setConfigForm((p) => ({ ...p, notas_negocio: e.target.value }))}
            style={S.textarea}
            placeholder="Informacion adicional del negocio..."
            readOnly={!puedeEditar}
          />
        </div>
      </div>
    );
  }

  function renderEditModal() {
    return (
      <div style={S.modalOverlay} onClick={() => setEditSkill(null)}>
        <div style={S.modal} onClick={(e) => e.stopPropagation()}>
          <div style={S.modalHeader}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: COL.pri }}>
              Editar conocimiento
            </div>
            <button onClick={() => setEditSkill(null)} style={S.btnIcon}>
              <FiX size={18} />
            </button>
          </div>
          <div style={{ padding: '20px' }}>
            <label style={S.label}>Titulo</label>
            <input
              value={editSkill.titulo}
              onChange={(e) => setEditSkill((p) => ({ ...p, titulo: e.target.value }))}
              style={S.input}
            />
            <label style={{ ...S.label, marginTop: '12px' }}>Contenido</label>
            <textarea
              value={editSkill.contenido}
              onChange={(e) => setEditSkill((p) => ({ ...p, contenido: e.target.value }))}
              style={{ ...S.textarea, minHeight: '250px' }}
            />
          </div>
          <div style={S.modalFooter}>
            <button onClick={() => setEditSkill(null)} style={S.btnGray}>
              Cancelar
            </button>
            <button onClick={guardarSkill} disabled={guardando} style={S.btnPrimary}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderNuevoModal() {
    return (
      <div style={S.modalOverlay} onClick={() => setNuevoSkill(null)}>
        <div style={S.modal} onClick={(e) => e.stopPropagation()}>
          <div style={S.modalHeader}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: COL.pri }}>
              Nuevo conocimiento
            </div>
            <button onClick={() => setNuevoSkill(null)} style={S.btnIcon}>
              <FiX size={18} />
            </button>
          </div>
          <div style={{ padding: '20px' }}>
            <label style={S.label}>Titulo</label>
            <input
              value={nuevoSkill.titulo}
              onChange={(e) => setNuevoSkill((p) => ({ ...p, titulo: e.target.value }))}
              style={S.input}
              placeholder="Ej: Promociones Activas"
            />
            <label style={{ ...S.label, marginTop: '12px' }}>Contenido</label>
            <div style={S.labelHint}>
              Escribe todo el conocimiento que el bot debe tener sobre este tema.
            </div>
            <textarea
              value={nuevoSkill.contenido}
              onChange={(e) => setNuevoSkill((p) => ({ ...p, contenido: e.target.value }))}
              style={{ ...S.textarea, minHeight: '250px' }}
              placeholder="Describe el conocimiento en detalle..."
            />
          </div>
          <div style={S.modalFooter}>
            <button onClick={() => setNuevoSkill(null)} style={S.btnGray}>
              Cancelar
            </button>
            <button
              onClick={crearSkill}
              disabled={guardando || !nuevoSkill.titulo || !nuevoSkill.contenido}
              style={S.btnPrimary}
            >
              {guardando ? 'Creando...' : 'Crear'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const S = {
  container: {
    padding: '20px',
    maxWidth: '960px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    height: '60vh',
    color: COL.muted,
    fontSize: '15px',
  },
  toast: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    color: COL.white,
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    zIndex: 10000,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  statBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    background: 'var(--agx-blue-bg)',
    fontSize: '12px',
    fontWeight: 600,
    color: COL.pri2,
  },

  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
    background: COL.white,
    padding: '4px',
    borderRadius: '10px',
    border: `1px solid ${COL.border}`,
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 18px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: COL.muted,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabActive: { background: COL.pri2, color: COL.white },

  mainGrid: { display: 'flex', gap: '20px' },
  content: { flex: 1, minWidth: 0 },

  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    gap: '12px',
    flexWrap: 'wrap',
  },
  sectionTitle: { fontSize: '16px', fontWeight: 700, color: COL.pri },
  sectionDesc: { fontSize: '13px', color: COL.muted, marginTop: '2px', maxWidth: '500px' },

  formCard: {
    background: COL.white,
    borderRadius: '10px',
    padding: '20px',
    border: `1px solid ${COL.border}`,
    marginBottom: '12px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: COL.text,
    marginBottom: '4px',
  },
  labelHint: { fontSize: '11px', color: COL.muted, marginBottom: '8px' },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '13px',
    color: COL.text,
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    fontSize: '13px',
    color: COL.text,
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    minHeight: '60px',
    boxSizing: 'border-box',
  },

  skillCard: {
    background: COL.white,
    borderRadius: '10px',
    padding: '14px 16px',
    border: `1px solid ${COL.border}`,
    marginBottom: '8px',
    transition: 'all 0.2s',
  },
  skillCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  skillPreview: { fontSize: '12px', color: COL.muted, marginTop: '6px', lineHeight: '1.4' },
  skillContent: { marginTop: '10px', maxHeight: '300px', overflow: 'auto' },
  skillPre: {
    fontSize: '12px',
    color: COL.text,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
    margin: 0,
    lineHeight: '1.5',
  },

  badgeBlue: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    background: 'var(--agx-blue-bg)',
    color: COL.pri2,
    fontSize: '10px',
    fontWeight: 700,
  },
  badgePurple: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    background: 'var(--agx-info-bg)',
    color: COL.purple,
    fontSize: '10px',
    fontWeight: 700,
  },
  badgeAmber: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    background: 'var(--agx-amber-bg)',
    color: COL.amber,
    fontSize: '10px',
    fontWeight: 700,
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '40px 20px',
    background: COL.white,
    borderRadius: '10px',
    border: `1px dashed ${COL.border}`,
    textAlign: 'center',
  },

  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: COL.pri2,
    color: COL.white,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnGray: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    background: COL.white,
    color: COL.text,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnIcon: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
  },
  btnSmallGreen: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    background: COL.acc,
    color: COL.white,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSmallGray: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    borderRadius: '6px',
    border: `1px solid ${COL.border}`,
    background: COL.white,
    color: COL.muted,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },

  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modal: {
    background: COL.white,
    borderRadius: '14px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: `1px solid ${COL.border}`,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '16px 20px',
    borderTop: `1px solid ${COL.border}`,
  },

  readonlyBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderRadius: '10px',
    background: 'var(--agx-amber-bg)',
    border: '1px solid var(--agx-amber)',
    color: 'var(--agx-warning-text)',
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: '16px',
  },
};

export default BotIA;
